import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Wrench, Handshake, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { HERO_IMAGE } from "@/lib/constants";
import { MaskLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import VehicleCard from "@/components/VehicleCard";
import SocialLinks from "@/components/SocialLinks";

const CHAPTERS = [
  {
    num: "01",
    title: "Sélection exigeante",
    text: "Chaque véhicule est choisi, inspecté et historisé avant d'entrer au catalogue. Provenance claire, factures et contrôle technique vérifiés.",
    icon: ShieldCheck,
  },
  {
    num: "02",
    title: "Préparation soignée",
    text: "Reconditionnement esthétique et mécanique dans nos ateliers de Chalon-sur-Saône pour une remise en main propre irréprochable.",
    icon: Wrench,
  },
  {
    num: "03",
    title: "Confiance totale",
    text: "Garantie annoncée sur chaque fiche, reprise de votre ancien véhicule et accompagnement après-vente. Vous repartez serein.",
    icon: Handshake,
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    api
      .get("/vehicles")
      .then((r) => setFeatured(r.data.filter((v) => v.status !== "vendu").slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden noise">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
          <img
            src={HERO_IMAGE}
            alt="Showroom Groupe Huvig"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full h-[120%] object-cover spotlight-img"
          />
        </motion.div>
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 -z-10 red-glow" />

        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-spec text-xs uppercase tracking-[0.35em] text-[#FF1E27] mb-6"
            data-testid="hero-eyebrow"
          >
            Achat · Vente · Revente — Chalon-sur-Saône
          </motion.p>

          <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-8xl">
            <MaskLine delay={0.35}>L'excellence</MaskLine>
            <MaskLine delay={0.5} className="text-[#FF1E27]">automobile</MaskLine>
            <MaskLine delay={0.65}>signée GHV.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed"
          >
            Groupe Huvig sélectionne, prépare et garantit des véhicules d'exception et des
            premium d'occasion. Chaque voiture est exposée avec son histoire, son prix et sa
            garantie — sans surprise.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/catalogue"
              data-testid="hero-cta-catalogue"
              className="h-12 px-8 rounded-full bg-[#FF1E27] text-white font-semibold flex items-center gap-2 hover:bg-[#E63946] transition-colors"
            >
              Découvrir le catalogue <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/vendre"
              data-testid="hero-cta-trade-in"
              className="h-12 px-8 rounded-full border border-white/25 text-white font-semibold flex items-center hover:border-[#FF1E27] hover:text-[#FF1E27] transition-colors"
            >
              Proposer mon véhicule
            </Link>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-zinc-500"
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </section>

      <Marquee />

      {/* SELECTION */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="font-spec text-xs uppercase tracking-[0.3em] text-[#FF1E27] mb-3">Le showroom</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">
                Sélection du moment
              </h2>
            </div>
            <Link
              to="/catalogue"
              data-testid="home-see-all-link"
              className="hidden sm:flex items-center gap-2 text-sm text-zinc-400 hover:text-[#FF1E27] transition-colors shrink-0"
            >
              Tout le catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>

          <div data-testid="home-featured-grid" className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featured.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.12}>
                <VehicleCard v={v} />
              </Reveal>
            ))}
          </div>

          <div className="mt-8 sm:hidden">
            <Link to="/catalogue" data-testid="home-see-all-link-mobile" className="flex items-center gap-2 text-sm text-zinc-400">
              Tout le catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-16 sm:py-24 border-t border-white/5 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="font-spec text-xs uppercase tracking-[0.3em] text-[#FF1E27] mb-3">Manifeste</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight mb-14">
              La méthode Groupe Huvig
            </h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-3">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.15}>
                <div className="border-t-2 border-[#FF1E27] pt-6" data-testid={`manifesto-chapter-${c.num}`}>
                  <p className="font-display text-5xl font-black text-white/10">{c.num}</p>
                  <c.icon className="w-6 h-6 text-[#FF1E27] mt-4 mb-3" />
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-3">{c.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL */}
      <section className="py-16 sm:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <p className="font-spec text-xs uppercase tracking-[0.3em] text-[#FF1E27] mb-3">Réseaux sociaux</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">
              Restez connectés
            </h2>
            <p className="mt-4 text-sm md:text-base text-zinc-400 max-w-lg mx-auto">
              Nouvelles arrivées, coulisses du showroom et ventes en avant-première : suivez le
              Groupe Huvig sur vos réseaux préférés.
            </p>
            <SocialLinks className="justify-center mt-8" itemClass="w-14 h-14" />
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 red-glow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight">
              Vous vendez <span className="text-[#FF1E27]">votre voiture ?</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-zinc-400 max-w-xl mx-auto">
              Estimation gratuite et sans engagement. Rachat cash ou dépôt-vente : envoyez-nous les
              détails, on s'occupe du reste.
            </p>
            <Link
              to="/vendre"
              data-testid="home-cta-trade-in-band"
              className="inline-flex items-center gap-2 mt-8 h-12 px-8 rounded-full bg-white text-black font-semibold hover:bg-[#FF1E27] hover:text-white transition-colors"
            >
              Estimation gratuite <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
